import { useCallback, useEffect, useRef, useState } from "react";
import { newClientId } from "./api";

export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

// Persists form state + client id in localStorage so drafts survive reloads and retries stay idempotent.
export function useDraft(key, initial) {
  const storageKey = `valeriia_draft:${key}`;
  const read = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  };
  const stored = useRef(read());
  const [state, setState] = useState(stored.current?.data ?? initial);
  const [clientId, setClientId] = useState(stored.current?.clientId ?? newClientId());
  const [pending, setPending] = useState(Boolean(stored.current?.pending));
  const [restored] = useState(Boolean(stored.current));

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({ data: state, clientId, pending }));
  }, [storageKey, state, clientId, pending]);

  const clear = useCallback(() => {
    localStorage.removeItem(storageKey);
    setClientId(newClientId());
    setPending(false);
  }, [storageKey]);

  const reset = useCallback((value) => {
    setState(value);
    localStorage.removeItem(storageKey);
    setClientId(newClientId());
    setPending(false);
  }, [storageKey]);

  return { state, setState, clientId, pending, setPending, clear, reset, restored };
}
